/**
 * useChatUiActions — the applyUiActions cluster from ChatBox.tsx.
 *
 * INVARIANTS: dependency arrays are load-bearing — the dead
 * refreshSession/updateProfile deps of applyUiActions control its recreate
 * cadence, which onCardAction and MessageList re-renders depend on. The hook is
 * called at the position of the FIRST moved hook so ChatBox's global hook order
 * is unchanged. State and the refs read elsewhere in ChatBox stay owned by
 * ChatBox and thread in by reference.
 *
 * Pinned by ChatBox.render.test.tsx: CN-8 ('xavior:apply-ui-actions'
 * listener), CN-11 (stream → applyUiActions → onboarding/advance POST),
 * CN-13 (conversation-command focusModule + negative), CN-14 (onCardAction
 * email open / email_draft send), CN-10 (route-focus effect).
 */
import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';
import type { WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import { type ResultCardKind } from '@/features/chat/components/result-cards/types';
import type { Module } from '@/types/modules';
import {
  addWindowListener,
  conversationEvents,
  removeWindowListener,
} from '@/lib/conversation-events';
import { normalizeModuleLabelKey } from '@/features/chat/utils/chat-text';
import {
  VIEW_OPEN_ACK_TEMPLATES,
  VIEW_CREATE_ACK_TEMPLATES,
} from '../components/chatbox/constants';
import { runApplyUiActions } from '../components/chatbox/apply-ui-actions';
import { runCardAction } from '../components/chatbox/card-actions';


type FocusModule = (
  moduleId: string,
  options?: { anchorMessageId?: string | null; shouldScrollIntoView?: boolean },
) => void;

export interface UseChatUiActionsDeps {
  activeConversation: { id?: string; name?: string } | null | undefined;
  availableModules: Module[];
  token: string | null;
  workspaceId: string;
  queryClient: QueryClient;
  navigate: NavigateFunction;
  selectObject: (type: any, id: string) => void;
  // Optional `type` so the one threaded function satisfies BOTH delegate
  // signatures: runApplyUiActions wants (message, type) and runCardAction
  // wants (message, type?) — a required-2nd-param fn is not assignable to the
  // latter under strict.
  // 4-arg form (errorContext, action) matches ToastContext + apply-ui-actions so
  // the #5539 "Open in Studio" CTA isn't silently dropped by a narrower wrapper.
  showToast: (
    message: string,
    type?: 'success' | 'error' | 'info',
    errorContext?: { message: string; context?: string },
    action?: { label: string; onClick: () => void },
  ) => void;
  /** Threaded for the contact `add` card intent (unresolved invitee → create). */
  sendMessage: (content: string, conversationId?: string) => Promise<void> | void;
  expandChatPane: () => void;
  deleteConversation: (conversationId: string) => Promise<void>;
  refreshWorkspaceModuleCatalog: () => Promise<WorkspaceModule[]>;
  ensureModulePanelInConversationOnce: (conversationId: string, moduleId: string) => Promise<boolean>;
  refreshPanelBoundModules: (
    conversationId: string,
    options?: { forceRefresh?: boolean },
  ) => Promise<Module[]>;
  invalidateConversationStructure: (conversationId: string) => void;
  queuePendingModuleFocus: (focus: {
    conversationId: string;
    moduleId: string;
    anchorMessageId: string | null;
    sequence: number;
  }) => void;
  /** Dead deps of applyUiActions kept for byte-identical recreate cadence. */
  refreshSession: () => Promise<unknown> | unknown;
  updateProfile: (...args: any[]) => any;
  // Route params (route-module-focus effect)
  requestedRouteModuleId: string | null;
  routeConversationIdParam: string | undefined;
  // State setters owned by ChatBox
  setObjectCardPopover: (value: { objectType: string; objectId: string } | null) => void;
  setNoteModal: (value: { noteId: string } | null) => void;
  setDocModal: (value: { docId: string } | null) => void;
  setIsLocalViewIntentThinking: Dispatch<SetStateAction<boolean>>;
  // Refs owned by ChatBox (read/written elsewhere too)
  workspaceModuleCatalogRef: MutableRefObject<WorkspaceModule[]>;
  moduleFocusSequenceRef: MutableRefObject<number>;
  lastAppliedModuleFocusSequenceRef: MutableRefObject<number>;
  activeConversationIdRef: MutableRefObject<string | null>;
  focusModuleRef: MutableRefObject<FocusModule>;
  viewIntentPhraseIndexRef: MutableRefObject<number>;
  localViewIntentTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
}

export interface UseChatUiActionsResult {
  applyUiActions: (actions: ChatUIAction[]) => void;
  applyUiActionsRef: MutableRefObject<(actions: ChatUIAction[]) => void>;
  onCardAction: (a: { kind: ResultCardKind; intent: 'open' | 'send' | 'compose' | 'add'; data: any }) => unknown;
  openWorkspaceModuleByLabel: (
    label: string,
    anchorMessageId?: string,
    occurrenceIndex?: number,
  ) => Promise<void>;
  getNextViewAckMessage: (viewLabel: string, mode: 'open' | 'switch' | 'create') => string;
  runLocalViewIntentAction: (action: () => void) => void;
  lastRouteModuleFocusKeyRef: MutableRefObject<string | null>;
}

export function useChatUiActions({
  activeConversation,
  availableModules,
  token,
  workspaceId,
  queryClient,
  navigate,
  selectObject,
  showToast,
  sendMessage,
  expandChatPane,
  deleteConversation,
  refreshWorkspaceModuleCatalog,
  ensureModulePanelInConversationOnce,
  refreshPanelBoundModules,
  invalidateConversationStructure,
  queuePendingModuleFocus,
  refreshSession,
  updateProfile,
  requestedRouteModuleId,
  routeConversationIdParam,
  setObjectCardPopover,
  setNoteModal,
  setDocModal,
  setIsLocalViewIntentThinking,
  workspaceModuleCatalogRef,
  moduleFocusSequenceRef,
  lastAppliedModuleFocusSequenceRef,
  activeConversationIdRef,
  focusModuleRef,
  viewIntentPhraseIndexRef,
  localViewIntentTimerRef,
}: UseChatUiActionsDeps): UseChatUiActionsResult {
  const applyUiActions = useCallback((actions: ChatUIAction[]) => {
    runApplyUiActions(actions, {
      activeConversation,
      availableModules,
      token,
      workspaceId,
      queryClient,
      navigate,
      selectObject,
      showToast,
      expandChatPane,
      deleteConversation,
      refreshWorkspaceModuleCatalog,
      ensureModulePanelInConversationOnce,
      refreshPanelBoundModules,
      invalidateConversationStructure,
      queuePendingModuleFocus,
      workspaceModuleCatalogRef,
      moduleFocusSequenceRef,
      lastAppliedModuleFocusSequenceRef,
      activeConversationIdRef,
      applyUiActionsRef,
      focusModuleRef,
    });
  }, [
    activeConversation?.id,
    availableModules,
    deleteConversation,
    ensureModulePanelInConversationOnce,
    invalidateConversationStructure,
    navigate,
    queuePendingModuleFocus,
    refreshPanelBoundModules,
    refreshSession,
    selectObject,
    showToast,
    updateProfile,
  ]);

  // Result-card click handler (e.g. a matched email acted on). setObjectCardPopover
  // (stable setter) and workspaceModuleCatalogRef (stable ref) are intentionally
  // not in the deps.
  const onCardAction = useCallback(
    (a: { kind: ResultCardKind; intent: 'open' | 'send' | 'compose' | 'add'; data: any }) => {
      return runCardAction(a, {
        applyUiActions,
        activeConversationId: activeConversation?.id,
        showToast,
        token,
        setObjectCardPopover,
        setNoteModal,
        setDocModal,
        workspaceModuleCatalogRef,
        sendMessage,
      });
    },
    [applyUiActions, activeConversation?.id, showToast, token, sendMessage],
  );

  // NOTE: the handler body below reads applyUiActionsRef, declared ~35 lines
  // further down. The forward reference is safe: useRef executes synchronously
  // in this same render pass, before any effect or event handler can fire. Do
  // NOT move the applyUiActionsRef declaration below this hook's return — the
  // handler would then throw at event time.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        action?: string;
        moduleId?: string;
        conversationId?: string;
        anchorMessageId?: string | null;
        source?: string;
      } | undefined;

      if (!detail || detail.source === 'chatbox') return;
      if (detail.action !== 'focusModule' || typeof detail.moduleId !== 'string') return;
      if (detail.moduleId.startsWith('plugin:')) return;
      if (detail.conversationId && activeConversation?.id && detail.conversationId !== activeConversation.id) return;

      const anchorMessageId = typeof detail.anchorMessageId === 'string'
        ? detail.anchorMessageId
        : undefined;
      applyUiActionsRef.current([{
        type: 'module.focus',
        moduleId: detail.moduleId,
        conversationId: detail.conversationId ?? activeConversation?.id ?? undefined,
        anchorMessageId,
      }]);
    };

    addWindowListener(conversationEvents.command, handler as EventListener);
    return () => {
      removeWindowListener(conversationEvents.command, handler as EventListener);
    };
  }, [activeConversation?.id]);

  // Stable ref for applyUiActions so the route-module-focus effect doesn't re-fire
  // when applyUiActions is recreated (it depends on availableModules which changes
  // on every React Query refetch, causing infinite re-render loops).
  const applyUiActionsRef = useRef(applyUiActions);
  applyUiActionsRef.current = applyUiActions;

  // Listen for external UI action requests (e.g. from notification bell click-through)
  useEffect(() => {
    const handler = (e: Event) => {
      const actions = (e as CustomEvent).detail;
      if (Array.isArray(actions)) applyUiActionsRef.current(actions);
    };
    window.addEventListener('xavior:apply-ui-actions', handler);
    return () => window.removeEventListener('xavior:apply-ui-actions', handler);
  }, []);

  const lastRouteModuleFocusKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeConversation?.id) {
      lastRouteModuleFocusKeyRef.current = null;
      return;
    }
    if (!requestedRouteModuleId || requestedRouteModuleId.startsWith('plugin:')) {
      lastRouteModuleFocusKeyRef.current = null;
      return;
    }
    if (routeConversationIdParam && routeConversationIdParam !== activeConversation.id) {
      return;
    }

    const routeKey = `${activeConversation.id}:${requestedRouteModuleId}`;
    if (lastRouteModuleFocusKeyRef.current === routeKey) return;
    lastRouteModuleFocusKeyRef.current = routeKey;

    applyUiActionsRef.current([{
      type: 'module.focus',
      moduleId: requestedRouteModuleId,
      conversationId: activeConversation.id,
    }]);
  }, [activeConversation?.id, requestedRouteModuleId, routeConversationIdParam]);

  const openWorkspaceModuleByLabel = useCallback(async (
    label: string,
    anchorMessageId?: string,
    occurrenceIndex?: number,
  ) => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return;
    const normalizedLabel = normalizeModuleLabelKey(trimmedLabel);

    const resolveMatches = (catalog: WorkspaceModule[]) => catalog.filter((module) => (
      !module.archivedAt
      && typeof module.label === 'string'
      && normalizeModuleLabelKey(module.label) === normalizedLabel
    ));

    let matches = resolveMatches(workspaceModuleCatalogRef.current);
    if (matches.length === 0) {
      const refreshed = await refreshWorkspaceModuleCatalog();
      matches = resolveMatches(refreshed);
    }

    if (matches.length === 0) {
      showToast(`Could not find a module named "${trimmedLabel}" in this workspace.`, 'info');
      return;
    }

    const explicitIndex = Number.isInteger(occurrenceIndex)
      ? Number(occurrenceIndex)
      : undefined;
    let target = matches[0];

    if (matches.length > 1) {
      if (explicitIndex !== undefined && explicitIndex >= 0 && explicitIndex < matches.length) {
        target = matches[explicitIndex];
      } else {
        // If the click did not carry a resolved occurrence index, fall back to the first
        // workspace match instead of showing an interruption toast.
        target = matches[0];
      }
    }

    applyUiActions([{
      type: 'module.focus',
      moduleId: target.id,
      anchorMessageId: anchorMessageId ?? null,
    } as ChatUIAction]);
  }, [applyUiActions, refreshWorkspaceModuleCatalog, showToast]);

  const getNextViewAckMessage = useCallback((viewLabel: string, mode: 'open' | 'switch' | 'create') => {
    const templates = mode === 'create' ? VIEW_CREATE_ACK_TEMPLATES : VIEW_OPEN_ACK_TEMPLATES;
    const nextIndex = viewIntentPhraseIndexRef.current % templates.length;
    viewIntentPhraseIndexRef.current += 1;
    return templates[nextIndex].replace('{view}', viewLabel);
  }, []);

  const runLocalViewIntentAction = useCallback((action: () => void) => {
    if (localViewIntentTimerRef.current) {
      clearTimeout(localViewIntentTimerRef.current);
      localViewIntentTimerRef.current = null;
    }

    const delayMs = 1000;
    setIsLocalViewIntentThinking(true);
    localViewIntentTimerRef.current = setTimeout(() => {
      localViewIntentTimerRef.current = null;
      setIsLocalViewIntentThinking(false);
      action();
    }, delayMs);
  }, []);

  return {
    applyUiActions,
    applyUiActionsRef,
    onCardAction,
    openWorkspaceModuleByLabel,
    getNextViewAckMessage,
    runLocalViewIntentAction,
    lastRouteModuleFocusKeyRef,
  };
}
